<template>
  <div>
    <TopNav />

    <main class="dashboard">
      <div class="dashboard-header dashboard-header-row">
        <div>
          <h1 class="dashboard-title">Fehlerbilder</h1>
          <p class="dashboard-subtitle">Stammdaten der Fehlerbilder für Reparatur-Rezepturen.</p>
        </div>
        <button class="btn btn-primary" @click="openCreateModal" :disabled="loading">
          Neues Fehlerbild
        </button>
      </div>

      <div v-if="loading" class="spinner" aria-label="Lädt"></div>
      <div v-else-if="loadError" class="error">{{ loadError }}</div>

      <div v-else-if="items.length === 0" class="empty-state">
        Keine Fehlerbilder vorhanden.
      </div>

      <div v-else class="table-responsive">
        <table class="table table-sm table-striped align-middle errorpattern-table">
          <thead>
            <tr>
              <th scope="col" style="width: 70px;">ID</th>
              <th scope="col">Bezeichnung</th>
              <th scope="col" class="ep-actions-cell"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="pattern in items" :key="pattern.id">
              <td data-label="ID">{{ pattern.id }}</td>
              <td data-label="Bezeichnung">{{ pattern.description }}</td>
              <td class="text-end ep-actions-cell" data-label="Aktionen">
                <button
                  class="btn btn-sm btn-outline-secondary me-1"
                  :disabled="busyId === pattern.id"
                  @click="openEditModal(pattern)"
                >Bearbeiten</button>
                <button
                  class="btn btn-sm btn-outline-danger"
                  :disabled="busyId === pattern.id"
                  @click="onDelete(pattern)"
                >Löschen</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </main>

    <!-- Bootstrap-Modal: Fehlerbild anlegen/bearbeiten -->
    <div
      ref="modalEl"
      class="modal fade"
      tabindex="-1"
      aria-labelledby="epModalTitle"
      aria-hidden="true"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <form @submit.prevent="onSubmit" novalidate>
            <div class="modal-header">
              <h5 class="modal-title" id="epModalTitle">
                {{ mode === 'edit' ? 'Fehlerbild bearbeiten' : 'Neues Fehlerbild' }}
              </h5>
              <button
                type="button"
                class="btn-close"
                data-bs-dismiss="modal"
                aria-label="Schließen"
              ></button>
            </div>

            <div class="modal-body">
              <div v-if="submitError" class="alert alert-danger" role="alert">
                {{ submitError }}
              </div>

              <div class="mb-1">
                <label for="ep-description" class="form-label">Bezeichnung</label>
                <input
                  id="ep-description"
                  ref="descriptionEl"
                  v-model="description"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': invalid }"
                  maxlength="64"
                  required
                />
              </div>
            </div>

            <div class="modal-footer">
              <button
                type="button"
                class="btn btn-secondary"
                data-bs-dismiss="modal"
                :disabled="submitting"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="submitting"
              >
                {{ submitting ? 'Speichert …' : 'Speichern' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, useTemplateRef } from 'vue';
import { Modal } from 'bootstrap';
import TopNav from '../components/TopNav.vue';
import {
  list,
  create,
  update,
  disable,
  ApiError,
  type ErrorPattern,
} from '../services/errorPatterns';

const items       = ref<ErrorPattern[]>([]);
const loading     = ref(true);
const loadError   = ref<string | null>(null);
const busyId      = ref<number | null>(null);

const mode        = ref<'create' | 'edit'>('create');
const editId      = ref<number | null>(null);
const description = ref('');
const submitting  = ref(false);
const submitError = ref<string | null>(null);
const invalid     = ref(false);

const modalEl       = useTemplateRef<HTMLDivElement>('modalEl');
const descriptionEl = useTemplateRef<HTMLInputElement>('descriptionEl');
let   modal: Modal | null = null;

onMounted(async () => {
  if (modalEl.value) {
    modal = new Modal(modalEl.value);
    // Fokus erst setzen, wenn das Modal wirklich sichtbar ist.
    modalEl.value.addEventListener('shown.bs.modal', () => descriptionEl.value?.focus());
  }
  await reload();
});

async function reload(): Promise<void> {
  loading.value   = true;
  loadError.value = null;
  try {
    items.value = await list();
  } catch (e) {
    loadError.value = errorMessage(e);
  } finally {
    loading.value = false;
  }
}

function openCreateModal(): void {
  mode.value        = 'create';
  editId.value      = null;
  description.value = '';
  submitError.value = null;
  invalid.value     = false;
  modal?.show();
}

function openEditModal(pattern: ErrorPattern): void {
  mode.value        = 'edit';
  editId.value      = pattern.id;
  description.value = pattern.description;
  submitError.value = null;
  invalid.value     = false;
  modal?.show();
}

async function onSubmit(): Promise<void> {
  submitError.value = null;
  invalid.value     = false;

  const value = description.value.trim();
  if (value === '') {
    invalid.value     = true;
    submitError.value = 'Bitte eine Bezeichnung eingeben.';
    return;
  }

  submitting.value = true;
  try {
    if (mode.value === 'edit' && editId.value !== null) {
      await update(editId.value, value);
    } else {
      await create(value);
    }
    modal?.hide();
    await reload();
  } catch (e) {
    // Namensdopplung (409) landet als Klartext-Meldung direkt beim Feld.
    if (e instanceof ApiError && e.code === 'CONFLICT') {
      invalid.value = true;
    }
    submitError.value = errorMessage(e);
  } finally {
    submitting.value = false;
  }
}

async function onDelete(pattern: ErrorPattern): Promise<void> {
  if (!confirm(`Fehlerbild „${pattern.description}“ wirklich löschen?`)) {
    return;
  }
  busyId.value = pattern.id;
  try {
    await disable(pattern.id);
    items.value = items.value.filter(x => x.id !== pattern.id);
  } catch (e) {
    alert(`Löschen fehlgeschlagen: ${errorMessage(e)}`);
  } finally {
    busyId.value = null;
  }
}

function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error)    return e.message;
  return 'Unbekannter Fehler.';
}
</script>

<style scoped>
.dashboard {
  max-width: 900px;
}

.ep-actions-cell {
  width: 220px;
  white-space: nowrap;
}

/* Kleine Bildschirme (< md): Tabelle als gestapelte Karten statt Querscroll */
@media (max-width: 767.98px) {
  .dashboard-header-row {
    flex-wrap: wrap;
    gap: .75rem;
  }
  .dashboard-header-row .btn {
    width: 100%;
  }

  .table-responsive {
    overflow-x: visible;
  }

  .errorpattern-table thead {
    display: none;
  }

  .errorpattern-table,
  .errorpattern-table tbody,
  .errorpattern-table tr,
  .errorpattern-table td {
    display: block;
    width: 100%;
  }

  .errorpattern-table tr {
    margin-bottom: 1rem;
    padding: .5rem .9rem;
    border: 1px solid var(--bs-border-color, #dee2e6);
    border-radius: .5rem;
    background: var(--bs-body-bg, #fff);
  }

  .errorpattern-table td {
    background-color: transparent !important;
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 1rem;
    padding: .35rem 0;
    border: none;
    border-bottom: 1px solid var(--bs-border-color-translucent, rgba(0, 0, 0, .075));
    text-align: right;
    word-break: break-word;
  }

  .errorpattern-table td:last-child {
    border-bottom: none;
  }

  .errorpattern-table td::before {
    content: attr(data-label);
    flex: 0 0 40%;
    font-weight: 600;
    text-align: left;
    color: var(--bs-secondary-color, #6c757d);
  }

  .errorpattern-table td.ep-actions-cell {
    width: 100%;
    white-space: normal;
    justify-content: stretch;
    gap: .5rem;
    padding-top: .6rem;
  }
  .errorpattern-table td.ep-actions-cell::before {
    content: none;
  }
  .errorpattern-table td.ep-actions-cell .btn {
    flex: 1 1 0;
    margin: 0 !important;
  }
}
</style>
